import type { z } from 'zod';
import { apiErrorSchema, AppError, type ErrorCode } from './errors';
import type {
  GoalChoice,
  MeasurementGuide,
  MeasurementGuideAdmin,
  MeasurementGuideInput,
  MeasurementVideoUrlInput,
  OnboardingBasicsInput,
  OnboardingGoalInput,
  TrialStatus,
} from './schemas/onboarding';
import type {
  AchievementRow, BusinessMetrics, ContentCategory, ContentDetail, ContentListItem,
  DiscountCodeRow, DiscountValidation, HealthMetric, HealthSampleInput, WeeklySummaryRow,
} from './phase3-types';
import type {
  CalendarItem, CheckInRow, ConversationRow, GoalRow, HabitRow, MessageRow,
  NotificationPreferenceRow, NotificationRow, NotificationType, SeriesMetric,
  SeriesOutput, SeriesRange, WeeklyClientRow,
} from './phase2-types';
import type {
  loginInput, registerInput, sessionOutput, checkoutInput, checkoutOutput,
  entitlementsOutput, portalSessionOutput, productOutput, faqOutput,
  testimonialOutput, contactInput, workoutLogInput, workoutCompletedOutput,
  syncWorkoutsInput, syncWorkoutsOutput, measurementInput, okSchema,
  programInput, weekInput, workoutInput, setWorkoutExercisesInput, exerciseInput,
  assignProgramInput, contentArticleInput, contentArticlePatch,
  meOutput, meUpdateInput, subscriptionOutput, dashboardOutput, todayOutput,
  weekPlanOutput, plannedWorkoutDetailOutput, dayNutritionOutput,
  onboardingStateOutput, mealInput, mealCreatedOutput, nutritionHistoryOutput, mealFeedbackInput,
  uploadRequestInput, uploadGrantOutput,
  progressPhotoConfirmInput, progressPhotoConfirmedOutput, progressPhotoListOutput,
  waterInput, waterOutput, mealPhotoConfirmInput, mealPhotoConfirmedOutput, frequentMealsOutput,
  mealTemplatesOutput,
} from './schemas/index';
import type { MealTemplateInput } from './schemas/nutrition';
import type {
  AdminContentArticle,
  AdminTransformation,
  TransformationInput,
  TransformationPatch,
  TransformationPhotoUrlInput,
} from './schemas/content';

type Out<T extends z.ZodTypeAny> = z.infer<T>;
type In<T extends z.ZodTypeAny> = z.input<T>;

/**
 * Oblici odgovora `/v1/trainer` i `/v1/admin` ruta.
 *
 * Rute vraćaju retke iz `@st/db` pa ovdje stoje kao tipovi, ne kao Zod sheme:
 * ulaz se i dalje validira shemama iz `schemas/programs.ts`, a izlaz je projekcija
 * tablice. Datumi su ISO nizovi jer `c.json` serijalizira `Date`.
 */
export interface ProgramRow {
  id: string;
  orgId: string;
  trainerId: string;
  nameHr: string;
  nameEn: string | null;
  descriptionHr: string | null;
  descriptionEn: string | null;
  weeksCount: number;
  level: number | null;
  isTemplate: boolean;
  publishedAt: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PrescribedSetRow {
  id: string;
  workoutExerciseId: string;
  setIndex: number;
  reps: string | null;
  weightKg: number | null;
  restSeconds: number | null;
  note: string | null;
}

export interface WorkoutExerciseRow {
  id: string;
  workoutId: string;
  exerciseId: string;
  sortOrder: number;
  sets: number | null;
  reps: string | null;
  weightHintKg: number | null;
  restSeconds: number | null;
  tempo: string | null;
  trainerNoteHr: string | null;
  trainerNoteEn: string | null;
  supersetGroup: string | null;
  prescribedSets: PrescribedSetRow[];
}

export interface WorkoutRow {
  id: string;
  programWeekId: string;
  dayIndex: number;
  nameHr: string;
  nameEn: string | null;
  type: 'strength' | 'cardio' | 'mobility' | 'rest' | 'mixed';
  estimatedMinutes: number | null;
  noteHr: string | null;
  noteEn: string | null;
  exercises: WorkoutExerciseRow[];
}

export interface ProgramWeekRow {
  id: string;
  programId: string;
  weekIndex: number;
  labelHr: string | null;
  labelEn: string | null;
  workouts: WorkoutRow[];
}

/** Vježbe koje stablo referencira — builder ne radi n+1 poziva na biblioteku. */
export interface ProgramTreeExercise {
  id: string;
  nameHr: string;
  nameEn: string;
  category: string;
  videoKey: string | null;
  imageKey: string | null;
  /** Potpisani URL sličice; `imageKey` je samo interni ključ u R2. */
  imageUrl: string | null;
}

export interface ProgramTree {
  program: ProgramRow;
  weeks: ProgramWeekRow[];
  exerciseLibrary: ProgramTreeExercise[];
}

export interface ExerciseRow {
  id: string;
  orgId: string;
  /** null = globalna biblioteka; mijenja je samo administrator. */
  trainerId: string | null;
  slug: string;
  nameHr: string;
  nameEn: string;
  category: string;
  muscleGroupsJson: string[] | null;
  equipment: string | null;
  /** Kako se vjezba mjeri — vidi `exerciseLoadTypes` u `@st/db`. */
  loadType: 'weighted' | 'bodyweight' | 'timed';
  difficulty: 'beginner' | 'intermediate' | 'advanced' | null;
  instructionsHr: string | null;
  instructionsEn: string | null;
  videoKey: string | null;
  imageKey: string | null;
  /** Potpisani URL sličice; `imageKey` je samo interni ključ u R2. */
  imageUrl: string | null;
  active: boolean;
}

export interface AdminPriceRow {
  id: string;
  productId: string;
  stripePriceId: string | null;
  currency: string;
  amountCents: number;
  compareAtCents: number | null;
  interval: 'one_time' | 'month' | 'quarter' | 'year';
  intervalCount: number;
  labelHr: string | null;
  labelEn: string | null;
  sortOrder: number;
  active: boolean;
}

export interface AdminProductRow {
  id: string;
  orgId: string;
  slug: string;
  nameHr: string;
  nameEn: string;
  descriptionHr: string | null;
  descriptionEn: string | null;
  featuresHrJson: string[] | null;
  featuresEnJson: string[] | null;
  accessDurationDays: number | null;
  deliveryMode: 'online' | 'gym' | 'hybrid';
  programId: string | null;
  /** Koliko je treninga besplatno prije plaćanja. 0 = nijedan. */
  trialWorkoutCount: number;
  stripeProductId: string | null;
  highlighted: boolean;
  sortOrder: number;
  active: boolean;
  prices: AdminPriceRow[];
}

export interface AdminProductInput {
  slug: string;
  nameHr: string;
  nameEn: string;
  descriptionHr?: string | null;
  descriptionEn?: string | null;
  featuresHr?: string[];
  featuresEn?: string[];
  accessDurationDays?: number | null;
  deliveryMode?: 'online' | 'gym' | 'hybrid';
  /** Koliko treninga klijent odradi prije nego se traži plaćanje. 0 = nijedan. */
  trialWorkoutCount?: number;
  /** Plan treninga koji klijent dobiva kupnjom. Bez njega paket nema program. */
  programId?: string | null;
  highlighted?: boolean;
  sortOrder?: number;
  active?: boolean;
}

/**
 * Nova cijena. Iznos je u centima jer novac nikad ne putuje kao decimalni broj.
 *
 * Kvartalna cijena zapisuje se kao `interval: 'month'` i `intervalCount: 3` —
 * tako je zapisuje i Stripe, pa se dva zapisa ne mogu razići.
 */
export interface AdminPriceInput {
  amountCents: number;
  currency?: string;
  compareAtCents?: number | null;
  interval: 'one_time' | 'month' | 'quarter' | 'year';
  intervalCount?: number;
  labelHr?: string | null;
  labelEn?: string | null;
  sortOrder?: number;
  active?: boolean;
}

/**
 * Što se na cijeni smije mijenjati nakon što je otišla u Stripe.
 *
 * Iznos, valuta i razdoblje nisu na popisu namjerno: Stripe cijene su
 * nepromjenjive, pa bi izmjena ovdje značila da sajt pokazuje jedan iznos, a
 * naplata skida drugi. Za promjenu iznosa postoji `replacePrice`.
 */
export interface AdminPriceEdit {
  compareAtCents?: number | null;
  labelHr?: string | null;
  labelEn?: string | null;
  sortOrder?: number;
  active?: boolean;
}

/**
 * Što bi sinkronizacija sa Stripeom napravila da se pokrene sada.
 * Oblik je prepisan iz `GET /v1/admin/stripe/sync/preview`, ne izmišljen.
 */
export interface StripeSyncPreview {
  items: {
    productId: string;
    slug: string;
    name: string;
    needsStripeProduct: boolean;
    prices: {
      priceId: string;
      amountCents: number;
      currency: string;
      interval: 'one_time' | 'month' | 'quarter' | 'year';
      intervalCount: number;
      needsStripePrice: boolean;
    }[];
  }[];
  pending: { products: number; prices: number };
}

export interface StripeSyncResult {
  created: { products: number; prices: number };
  skipped: { products: number; prices: number };
}

export interface AdminPaymentRow {
  id: string;
  userEmail: string;
  amountCents: number;
  currency: string;
  status: 'succeeded' | 'failed' | 'pending' | 'refunded';
  occurredAt: string;
  failureReason: string | null;
  stripePaymentIntentId: string | null;
}

export const submissionStatuses = ['new', 'contacted', 'converted', 'closed'] as const;
export type SubmissionStatus = (typeof submissionStatuses)[number];

export interface AdminSubmissionRow {
  id: string;
  orgId: string;
  name: string;
  email: string;
  phone: string | null;
  message: string | null;
  source: 'contact_form' | 'assessment' | 'other';
  /** Odgovori iz kalkulatora besplatne procjene. */
  assessmentJson: Record<string, unknown> | null;
  locale: 'hr' | 'en';
  status: SubmissionStatus;
  handledBy: string | null;
  internalNote: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Namjerno labav tip — isti klijent radi u pregledniku, na Workersima i u React Native. */
export type FetchLike = (input: string, init?: Record<string, unknown>) => Promise<Response>;

export interface ClientOptions {
  baseUrl: string;
  /** Vraća trenutni access token ili null. Osvježavanje rješava onUnauthorized. */
  getToken?: () => string | null | Promise<string | null>;
  /** Pozvano na 401. Vrati novi token da se zahtjev ponovi jednom, ili null. */
  onUnauthorized?: () => Promise<string | null>;
  locale?: 'hr' | 'en';
  fetchImpl?: FetchLike;
}

/**
 * Jedini način na koji frontend razgovara s API-jem.
 * Nigdje u aplikacijama ne postoji ručno napisan fetch prema /v1.
 */
export function createClient(opts: ClientOptions) {
  const f: FetchLike = opts.fetchImpl ?? ((input, init) => fetch(input, init as never));

  async function call<T>(
    method: string,
    path: string,
    body?: unknown,
    init: { idempotencyKey?: string; retry?: boolean } = {},
  ): Promise<T> {
    const token = await opts.getToken?.();
    const headers: Record<string, string> = {
      accept: 'application/json',
      'accept-language': opts.locale ?? 'hr',
    };
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (token) headers['authorization'] = `Bearer ${token}`;
    if (init.idempotencyKey) headers['idempotency-key'] = init.idempotencyKey;

    const res = await f(`${opts.baseUrl}${path}`, {
      method,
      headers,
      credentials: 'include',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });

    if (res.status === 401 && init.retry !== false && opts.onUnauthorized) {
      const fresh = await opts.onUnauthorized();
      if (fresh) return call<T>(method, path, body, { ...init, retry: false });
    }

    if (res.status === 204) return undefined as T;

    const text = await res.text();
    const json: unknown = text ? JSON.parse(text) : null;

    if (!res.ok) {
      const parsed = apiErrorSchema.safeParse(json);
      if (parsed.success) {
        throw new AppError(parsed.data.error.code, parsed.data.error.message, parsed.data.error.fields);
      }
      throw new AppError(mapStatus(res.status), `HTTP ${res.status}`);
    }
    return json as T;
  }

  /**
   * Slanje bajtova na potpisani URL koji je izdala neka `*-url` ruta.
   *
   * Postoji jer `call` šalje isključivo JSON, a prijenos datoteke ide kao `PUT`
   * sa sirovim tijelom na APSOLUTNI URL (`/v1/files` na API domeni). Bez ove
   * metode bi svaki ekran koji prenosi sliku morao napisati vlastiti `fetch`
   * prema `/v1` — točno ono što pravilo 3 zabranjuje.
   *
   * `/v1/files` odbija tijelo čiji stvarni tip ili veličina ne odgovaraju
   * potpisu, i to običnim tekstom, ne omotnicom greške — zato se status ovdje
   * prevodi u `AppError` s kodom na kojem sučelje bira poruku.
   */
  async function uploadFile(
    uploadUrl: string,
    file: Blob,
    init: { retry?: boolean } = {},
  ): Promise<void> {
    const token = await opts.getToken?.();
    const headers: Record<string, string> = {
      accept: 'application/json',
      'content-type': file.type,
    };
    if (token) headers['authorization'] = `Bearer ${token}`;

    const res = await f(uploadUrl, {
      method: 'PUT',
      headers,
      credentials: 'include',
      body: file,
    });

    if (res.status === 401 && init.retry !== false && opts.onUnauthorized) {
      const fresh = await opts.onUnauthorized();
      if (fresh) return uploadFile(uploadUrl, file, { ...init, retry: false });
    }

    if (!res.ok) {
      // 413 i 415 su odbijena datoteka, ne kvar sustava — korisnik smije pokušati drugu.
      const code: ErrorCode =
        res.status === 413 || res.status === 415 ? 'upload_failed' : mapStatus(res.status);
      throw new AppError(code, `HTTP ${res.status}`);
    }
  }

  return {
    auth: {
      register: (b: In<typeof registerInput>) => call<Out<typeof sessionOutput>>('POST', '/v1/auth/register', b),
      login: (b: In<typeof loginInput>) => call<Out<typeof sessionOutput>>('POST', '/v1/auth/login', b),
      refresh: () => call<Out<typeof sessionOutput>>('POST', '/v1/auth/refresh', undefined, { retry: false }),
      logout: () => call<Out<typeof okSchema>>('POST', '/v1/auth/logout'),
      forgot: (b: { email: string }) => call<Out<typeof okSchema>>('POST', '/v1/auth/forgot', b),
      reset: (b: { token: string; password: string }) => call<Out<typeof okSchema>>('POST', '/v1/auth/reset', b),
      verifyEmail: (b: { token: string }) => call<Out<typeof okSchema>>('POST', '/v1/auth/verify-email', b),
    },
    publicCatalog: {
      products: (locale: 'hr' | 'en' = 'hr') =>
        call<{ items: Out<typeof productOutput>[] }>('GET', `/v1/public/products?locale=${locale}`),
      faqs: (locale: 'hr' | 'en' = 'hr') =>
        call<{ items: Out<typeof faqOutput>[] }>('GET', `/v1/public/faqs?locale=${locale}`),
      testimonials: (locale: 'hr' | 'en' = 'hr') =>
        call<{ items: Out<typeof testimonialOutput>[] }>('GET', `/v1/public/testimonials?locale=${locale}`),
      contact: (b: In<typeof contactInput>) => call<Out<typeof okSchema>>('POST', '/v1/public/contact', b),
    },
    /**
     * Profil prijavljenog korisnika. `update` vraća `{ ok: true }`, ne novi
     * profil — pozivatelj mora ponovno dohvatiti `me`, ne pisati u cache.
     */
    me: {
      get: () => call<Out<typeof meOutput>>('GET', '/v1/me'),
      update: (b: In<typeof meUpdateInput>) => call<Out<typeof okSchema>>('PATCH', '/v1/me', b),
      /** Sažetak početnog ekrana živi pod `/v1/me`, ne pod `/v1/training`. */
      dashboard: () => call<Out<typeof dashboardOutput>>('GET', '/v1/me/dashboard'),
    },
    billing: {
      checkout: (b: In<typeof checkoutInput>) => call<Out<typeof checkoutOutput>>('POST', '/v1/billing/checkout', b),
      entitlements: () => call<Out<typeof entitlementsOutput>>('GET', '/v1/billing/entitlements'),
      customerPortal: () => call<Out<typeof portalSessionOutput>>('POST', '/v1/billing/portal'),
      subscription: () => call<Out<typeof subscriptionOutput>>('GET', '/v1/billing/subscription'),
    },
    training: {
      today: () => call<Out<typeof todayOutput>>('GET', '/v1/training/today'),
      plan: (week?: number) =>
        call<Out<typeof weekPlanOutput>>(
          'GET',
          week === undefined ? '/v1/training/plan' : `/v1/training/plan?week=${week}`,
        ),
      workout: (id: string) =>
        call<Out<typeof plannedWorkoutDetailOutput>>('GET', `/v1/training/workouts/${id}`),
      logWorkout: (b: In<typeof workoutLogInput>) =>
        call<Out<typeof workoutCompletedOutput>>('POST', '/v1/training/logs', b, {
          idempotencyKey: b.clientIdempotencyKey,
        }),
      sync: (b: In<typeof syncWorkoutsInput>) =>
        call<Out<typeof syncWorkoutsOutput>>('POST', '/v1/training/sync', b),
    },
    nutrition: {
      day: (day?: string) =>
        call<Out<typeof dayNutritionOutput>>(
          'GET',
          day === undefined ? '/v1/nutrition/day' : `/v1/nutrition/day?day=${day}`,
        ),
      /** Dnevnik unatrag, sažet po danu — sekcija 16, „look back through their food history". */
      setWater: (b: In<typeof waterInput>) =>
        call<Out<typeof waterOutput>>('PUT', '/v1/nutrition/water', b),
      /** Obroci koje klijent najčešće upisuje — složeno jelo se upisuje jednom. */
      frequent: () =>
        call<Out<typeof frequentMealsOutput>>('GET', '/v1/nutrition/frequent'),
      /** Predlosci trenera s kojim klijent ima aktivnu vezu. Prazno ako veze nema. */
      templates: () =>
        call<Out<typeof mealTemplatesOutput>>('GET', '/v1/nutrition/templates'),
      /** Potvrda fotografije obroka; zapis nastaje tek kad su bajtovi u R2. */
      confirmMealPhoto: (mealId: string, b: In<typeof mealPhotoConfirmInput>) =>
        call<Out<typeof mealPhotoConfirmedOutput>>(
          'POST', `/v1/nutrition/meals/${mealId}/photo`, b,
        ),
      history: () => call<Out<typeof nutritionHistoryOutput>>('GET', '/v1/nutrition/history'),
      /**
       * Upis obroka. `clientIdempotencyKey` je ujedno `Idempotency-Key` zaglavlje:
       * id zapisa nastaje pri stvaranju, ne pri slanju (pravilo 10), pa ponovno
       * slanje istog obroka nikad ne stvori duplikat.
       */
      addMeal: (b: In<typeof mealInput>) =>
        call<Out<typeof mealCreatedOutput>>('POST', '/v1/nutrition/meals', b, {
          idempotencyKey: b.clientIdempotencyKey,
        }),
      removeMeal: (id: string) =>
        call<Out<typeof okSchema>>('DELETE', `/v1/nutrition/meals/${id}`),
      /** Prvi korak prijenosa fotografije obroka; bajtovi idu kroz `files.upload`. */
      mealPhotoUrl: (mealId: string, b: In<typeof uploadRequestInput>) =>
        call<Out<typeof uploadGrantOutput>>(
          'POST',
          `/v1/nutrition/meals/${mealId}/photo-url`,
          b,
        ),
    },
    /**
     * Napredak. Fotografije su PRIVATNE: ovdje postoji samo klijentov pogled na
     * vlastite (`/v1/progress/photos`). Trenerov pogled je zasebna ruta koju
     * server pušta tek uz `coaching_relationships.photo_access_granted_at` —
     * ugovor je namjerno ne izlaže klijentskim ekranima.
     */
    progress: {
      addMeasurement: (b: In<typeof measurementInput>) =>
        call<{ id: string }>('POST', '/v1/progress/measurements', b),
      photos: () => call<Out<typeof progressPhotoListOutput>>('GET', '/v1/progress/photos'),
      /** Korak 1: dozvola za prijenos. Zapis u bazi još NE nastaje. */
      photoUploadUrl: (b: In<typeof uploadRequestInput>) =>
        call<Out<typeof uploadGrantOutput>>('POST', '/v1/progress/photos/upload-url', b),
      /**
       * Korak 3: potvrda. Server provjeri da objekt stvarno postoji u R2 pa tek
       * onda upiše redak — inače bi pao prijenos ostavio zapis s URL-om u 404.
       * Ponovljena potvrda istog ključa vraća postojeći zapis, ne duplikat.
       */
      confirmPhoto: (b: In<typeof progressPhotoConfirmInput>) =>
        call<Out<typeof progressPhotoConfirmedOutput>>('POST', '/v1/progress/photos', b),
    },
    /** Korak 2 svakog prijenosa: bajtovi na potpisani URL iz `*.photoUploadUrl`. */
    files: {
      upload: (uploadUrl: string, file: Blob) => uploadFile(uploadUrl, file),
    },
    /**
     * Program builder — `/v1/trainer`. Redoslijed vježbi se mijenja isključivo
     * kroz `setExercises`, zamjenom cijele liste (vidi `setWorkoutExercisesInput`).
     */
    trainerPrograms: {
      list: () => call<{ items: ProgramRow[] }>('GET', '/v1/trainer/programs'),
      create: (b: In<typeof programInput>) => call<{ id: string }>('POST', '/v1/trainer/programs', b),
      update: (id: string, b: Partial<In<typeof programInput>>) =>
        call<Out<typeof okSchema>>('PATCH', `/v1/trainer/programs/${id}`, b),
      /** Meko brisanje — dodijeljeni programi klijenata moraju preživjeti. */
      remove: (id: string) => call<Out<typeof okSchema>>('DELETE', `/v1/trainer/programs/${id}`),
      tree: (id: string) => call<ProgramTree>('GET', `/v1/trainer/programs/${id}`),

      addWeek: (programId: string, b: In<typeof weekInput> = {}) =>
        call<{ id: string; weekIndex: number }>('POST', `/v1/trainer/programs/${programId}/weeks`, b),
      duplicateWeek: (weekId: string) =>
        call<{ id: string; weekIndex: number }>('POST', `/v1/trainer/weeks/${weekId}/duplicate`),
      deleteWeek: (weekId: string) =>
        call<Out<typeof okSchema>>('DELETE', `/v1/trainer/weeks/${weekId}`),

      addWorkout: (weekId: string, b: In<typeof workoutInput>) =>
        call<{ id: string }>('POST', `/v1/trainer/weeks/${weekId}/workouts`, b),
      updateWorkout: (id: string, b: Partial<In<typeof workoutInput>>) =>
        call<Out<typeof okSchema>>('PATCH', `/v1/trainer/workouts/${id}`, b),
      duplicateWorkout: (id: string) =>
        call<{ id: string; dayIndex: number }>('POST', `/v1/trainer/workouts/${id}/duplicate`),
      deleteWorkout: (id: string) =>
        call<Out<typeof okSchema>>('DELETE', `/v1/trainer/workouts/${id}`),

      setExercises: (workoutId: string, b: In<typeof setWorkoutExercisesInput>) =>
        call<{ ok: true; count: number }>('PUT', `/v1/trainer/workouts/${workoutId}/exercises`, b),

      exercises: () => call<{ items: ExerciseRow[] }>('GET', '/v1/trainer/exercises'),
      createExercise: (b: In<typeof exerciseInput>) =>
        call<{ id: string }>('POST', '/v1/trainer/exercises', b),
      updateExercise: (id: string, b: Partial<In<typeof exerciseInput>>) =>
        call<Out<typeof okSchema>>('PATCH', `/v1/trainer/exercises/${id}`, b),

      assign: (programId: string, b: In<typeof assignProgramInput>) =>
        call<{ id: string }>('POST', `/v1/trainer/programs/${programId}/assign`, b),

      /**
       * Predlosci obroka. Jelovnik koji trener slozi jednom, a klijenti biraju
       * jednim dodirom umjesto da sami racunaju kalorije.
       */
      mealTemplates: () =>
        call<Out<typeof mealTemplatesOutput>>('GET', '/v1/trainer/meal-templates'),
      createMealTemplate: (b: MealTemplateInput) =>
        call<{ id: string }>('POST', '/v1/trainer/meal-templates', b),
      updateMealTemplate: (id: string, b: MealTemplateInput) =>
        call<Out<typeof okSchema>>('PATCH', `/v1/trainer/meal-templates/${id}`, b),
      deleteMealTemplate: (id: string) =>
        call<Out<typeof okSchema>>('DELETE', `/v1/trainer/meal-templates/${id}`),

      /**
       * Uputa „kako se mjeri". Trener i admin je uređuju na istom mjestu —
       * postavi se jednom, ali se mora moći zamijeniti kad se snimi bolja.
       */
      measurementGuide: () =>
        call<MeasurementGuideAdmin>('GET', '/v1/trainer/measurement-guide'),
      saveMeasurementGuide: (b: MeasurementGuideInput) =>
        call<Out<typeof okSchema>>('PUT', '/v1/trainer/measurement-guide', b),
      /** Potpisana dozvola za upload snimke; sam prijenos ide na `uploadUrl`. */
      measurementVideoUrl: (b: MeasurementVideoUrlInput) =>
        call<{ key: string; uploadUrl: string; expiresAt: string; maxBytes: number }>(
          'POST',
          '/v1/trainer/measurement-guide/video-url',
          b,
        ),

      /**
       * Transformacije („prije / poslije") koje idu na javni sajt. Prikuplja ih
       * trener, pa rute stoje ovdje, a ne pod `admin`.
       */
      transformations: () =>
        call<{ items: AdminTransformation[] }>('GET', '/v1/trainer/transformations'),
      createTransformation: (b: TransformationInput) =>
        call<AdminTransformation>('POST', '/v1/trainer/transformations', b),
      updateTransformation: (id: string, b: TransformationPatch) =>
        call<AdminTransformation>('PATCH', `/v1/trainer/transformations/${id}`, b),
      publishTransformation: (id: string) =>
        call<AdminTransformation>('POST', `/v1/trainer/transformations/${id}/publish`),
      unpublishTransformation: (id: string) =>
        call<AdminTransformation>('POST', `/v1/trainer/transformations/${id}/unpublish`),
      removeTransformation: (id: string) =>
        call<Out<typeof okSchema>>('DELETE', `/v1/trainer/transformations/${id}`),
      /** Potpisana dozvola za upload fotografije; sam prijenos ide na `uploadUrl`. */
      transformationPhotoUrl: (id: string, b: TransformationPhotoUrlInput) =>
        call<{ key: string; uploadUrl: string; expiresAt: string; maxBytes: number }>(
          'POST',
          `/v1/trainer/transformations/${id}/photo-url`,
          b,
        ),
    },
    /**
     * Uvid trenera u podatke faze 3 za NJEGOVOG klijenta. Oblici su isti kao u
     * `summaries` i `achievements` za prijavljenog klijenta — razlikuje se samo
     * čiji se `clientId` gleda, a server prije odgovora traži aktivnu vezu u
     * `coaching_relationships`.
     */
    trainerClients: {
      summaries: (clientId: string) =>
        call<{ clientId: string; items: WeeklySummaryRow[] }>(
          'GET',
          `/v1/trainer/clients/${clientId}/summaries`,
        ),
      achievements: (clientId: string) =>
        call<{ clientId: string; items: AchievementRow[] }>(
          'GET',
          `/v1/trainer/clients/${clientId}/achievements`,
        ),
      /**
       * Dnevnik prehrane klijenta — isti oblik kao klijentov `/v1/nutrition/day`.
       * Fotografije obroka klijent šalje da ih trener pregleda, pa ne stoje iza
       * pristanka za fotografije napretka; to su dvije različite stvari.
       */
      nutrition: (clientId: string, day?: string) =>
        call<Out<typeof dayNutritionOutput>>(
          'GET',
          day === undefined
            ? `/v1/trainer/clients/${clientId}/nutrition`
            : `/v1/trainer/clients/${clientId}/nutrition?day=${day}`,
        ),
      /** Koji dani uopće imaju upisan obrok — bez toga trener lista prazne dane. */
      nutritionHistory: (clientId: string) =>
        call<Out<typeof nutritionHistoryOutput>>(
          'GET',
          `/v1/trainer/clients/${clientId}/nutrition/history`,
        ),
      mealFeedback: (mealId: string, b: In<typeof mealFeedbackInput>) =>
        call<Out<typeof okSchema>>('PATCH', `/v1/trainer/meals/${mealId}`, b),
    },
    admin: {
      /**
       * Ručno odobrenje pristupa — uplata na račun ili na ruke, i sve dok
       * naplata kroz Stripe nije uključena. Bilješka je obavezna jer je to
       * odluka o novcu koju netko mora moći objasniti kasnije.
       */
      grantAccess: (b: {
        userId: string;
        productId: string;
        activeUntil?: string | null;
        note: string;
      }) => call<Out<typeof okSchema>>('POST', '/v1/admin/access', b),
      revokeAccess: (b: { userId: string; note: string }) =>
        call<Out<typeof okSchema>>('POST', '/v1/admin/access/revoke', b),

      products: () => call<{ items: AdminProductRow[] }>('GET', '/v1/admin/products'),
      createProduct: (b: AdminProductInput) => call<{ id: string }>('POST', '/v1/admin/products', b),
      updateProduct: (id: string, b: Partial<AdminProductInput>) =>
        call<Out<typeof okSchema>>('PATCH', `/v1/admin/products/${id}`, b),

      createPrice: (productId: string, b: AdminPriceInput) =>
        call<{ id: string }>('POST', `/v1/admin/products/${productId}/prices`, b),
      /**
       * Mijenja samo ono što je i nakon Stripea sigurno mijenjati — oznaku,
       * redoslijed, precrtanu cijenu i je li aktivna. Iznos ide kroz
       * `replacePrice`, jer ga Stripe ne dopušta mijenjati.
       */
      updatePrice: (id: string, b: AdminPriceEdit) =>
        call<Out<typeof okSchema>>('PATCH', `/v1/admin/prices/${id}`, b),
      /**
       * Promjena iznosa: stvara NOVU cijenu i gasi staru u istom koraku.
       * Vraća id nove cijene. Stara ostaje u bazi jer je vezana uz postojeće
       * narudžbe i pretplate.
       */
      replacePrice: (id: string, b: AdminPriceInput) =>
        call<{ id: string }>('POST', `/v1/admin/prices/${id}/replace`, b),

      stripeSyncPreview: () =>
        call<StripeSyncPreview>('GET', '/v1/admin/stripe/sync/preview'),
      stripeSync: () => call<StripeSyncResult>('POST', '/v1/admin/stripe/sync'),

      payments: () => call<{ items: AdminPaymentRow[] }>('GET', '/v1/admin/payments'),
      /** Stripe pokreće povrat; `charge.refunded` webhook ga potvrđuje. */
      refund: (paymentId: string, b: { amountCents?: number; reason?: string } = {}) =>
        call<{ refundId: string; status: string }>('POST', `/v1/admin/payments/${paymentId}/refund`, b),

      submissions: () => call<{ items: AdminSubmissionRow[] }>('GET', '/v1/admin/submissions'),
      updateSubmission: (id: string, b: { status?: SubmissionStatus; internalNote?: string | null }) =>
        call<Out<typeof okSchema>>('PATCH', `/v1/admin/submissions/${id}`, b),
    },
    /**
     * Edukacijski sadržaj — admin strana. `list` vraća i skice, za razliku od
     * `content.list`, koji je klijentov pogled na objavljeno.
     */
    adminContent: {
      list: () => call<{ items: AdminContentArticle[] }>('GET', '/v1/admin/content'),
      create: (b: In<typeof contentArticleInput>) => call<{ id: string }>('POST', '/v1/admin/content', b),
      update: (id: string, b: In<typeof contentArticlePatch>) =>
        call<Out<typeof okSchema>>('PATCH', `/v1/admin/content/${id}`, b),
      publish: (id: string) => call<Out<typeof okSchema>>('POST', `/v1/admin/content/${id}/publish`),
      unpublish: (id: string) => call<Out<typeof okSchema>>('POST', `/v1/admin/content/${id}/unpublish`),
      /** Tvrdo brisanje — na članak ne pokazuje nijedna tablica, trag ostaje u `audit_log`. */
      remove: (id: string) => call<Out<typeof okSchema>>('DELETE', `/v1/admin/content/${id}`),
    },
    onboarding: {
      /** Gdje je korisnik stao u čarobnjaku; 404 znači „kreni od nule". */
      state: () => call<Out<typeof onboardingStateOutput>>('GET', '/v1/onboarding/state'),
      questions: () => call<{ items: unknown[] }>('GET', '/v1/onboarding/questions'),
      answers: (b: { answers: Record<string, unknown>; completed?: boolean; step?: number }) =>
        call<Out<typeof okSchema>>('POST', '/v1/onboarding/answers', b),
      basics: (b: OnboardingBasicsInput) => call<Out<typeof okSchema>>('POST', '/v1/onboarding/basics', b),
      goalChoices: () => call<{ items: GoalChoice[] }>('GET', '/v1/onboarding/goal-choices'),
      goal: (b: OnboardingGoalInput) => call<Out<typeof okSchema>>('POST', '/v1/onboarding/goal', b),
      measurementGuide: () => call<MeasurementGuide>('GET', '/v1/onboarding/measurement-guide'),
    },
    trial: {
      /** Odabir programa bez plaćanja — otključava prvih N treninga. */
      chooseProgram: (productSlug: string, startDate?: string) =>
        call<{ assignmentId: string; trialWorkoutCount: number; alreadyActive: boolean }>(
          'POST', '/v1/training/choose-program', { productSlug, startDate },
        ),
      status: () => call<TrialStatus>('GET', '/v1/training/trial-status'),
    },
    messages: {
      conversations: () => call<{ items: ConversationRow[] }>('GET', '/v1/messages'),
      open: (partnerId?: string) =>
        call<{ id: string }>('POST', '/v1/messages/open', partnerId ? { partnerId } : {}),
      history: (conversationId: string, limit = 50) =>
        call<{ items: MessageRow[] }>('GET', `/v1/messages/${conversationId}/messages?limit=${limit}`),
      send: (conversationId: string, b: { body?: string; attachmentKey?: string; clientIdempotencyKey?: string }) =>
        call<{ id: string; createdAt: string; duplicate: boolean }>(
          'POST', `/v1/messages/${conversationId}/messages`, b,
          b.clientIdempotencyKey ? { idempotencyKey: b.clientIdempotencyKey } : {},
        ),
      markRead: (conversationId: string) =>
        call<Out<typeof okSchema>>('POST', `/v1/messages/${conversationId}/read`),
      /** WebSocket URL; token ide u query jer handshake nema Authorization zaglavlje. */
      socketUrl: (baseUrl: string, conversationId: string, accessToken: string) =>
        `${baseUrl.replace(/^http/, 'ws')}/v1/messages/${conversationId}/socket?token=${encodeURIComponent(accessToken)}`,
    },
    notifications: {
      list: (filter: 'all' | 'unread' = 'all', limit = 30) =>
        call<{ items: NotificationRow[] }>('GET', `/v1/notifications?filter=${filter}&limit=${limit}`),
      unreadCount: () => call<{ count: number }>('GET', '/v1/notifications/unread-count'),
      markRead: (id: string) => call<Out<typeof okSchema>>('POST', `/v1/notifications/${id}/read`),
      markUnread: (id: string) => call<Out<typeof okSchema>>('POST', `/v1/notifications/${id}/unread`),
      markAllRead: () => call<Out<typeof okSchema>>('POST', '/v1/notifications/read-all'),
      preferences: () => call<{ items: NotificationPreferenceRow[] }>('GET', '/v1/notifications/preferences'),
      setPreference: (b: { type: NotificationType; push?: boolean; email?: boolean }) =>
        call<Out<typeof okSchema>>('PUT', '/v1/notifications/preferences', b),
      /** Probna obavijest samom sebi; vraća što je Expo odgovorio. */
      test: () =>
        call<{ sent: number; tokens: number; reason?: string; errors?: string[] }>(
          'POST', '/v1/notifications/test',
        ),
      /**
       * Javni VAPID ključ za pretplatu preglednika. `null` znači da web push
       * na ovom okruženju nije postavljen — portal tada ni ne nudi prekidač,
       * umjesto da traži dopuštenje koje ne vodi nikamo.
       */
      webPushKey: () => call<{ publicKey: string | null }>('GET', '/v1/notifications/web-push-key'),
      /** Za web `token` je endpoint pretplate, a `p256dh` i `auth` njezini ključevi. */
      registerPushToken: (b: {
        token: string;
        platform: 'ios' | 'android' | 'web';
        p256dh?: string;
        auth?: string;
      }) => call<{ ok: true; moved: boolean }>('POST', '/v1/notifications/push-token', b),
      removePushToken: (token: string) =>
        call<Out<typeof okSchema>>('DELETE', '/v1/notifications/push-token', { token }),
    },
    habits: {
      list: (days = 30) => call<{ items: HabitRow[]; from: string; to: string }>('GET', `/v1/habits?days=${days}`),
      create: (b: { key: string; labelHr: string; labelEn?: string | null; targetValue?: number | null; unit?: string | null }) =>
        call<{ id: string }>('POST', '/v1/habits', b),
      remove: (id: string) => call<Out<typeof okSchema>>('DELETE', `/v1/habits/${id}`),
      log: (id: string, b: { loggedOn: string; done?: boolean; value?: number | null }) =>
        call<Out<typeof okSchema>>('PUT', `/v1/habits/${id}/log`, b),
    },
    goals: {
      list: () => call<{ items: GoalRow[] }>('GET', '/v1/goals'),
      create: (b: Record<string, unknown>) => call<{ id: string }>('POST', '/v1/goals', b),
      update: (id: string, b: Record<string, unknown>) => call<Out<typeof okSchema>>('PATCH', `/v1/goals/${id}`, b),
      remove: (id: string) => call<Out<typeof okSchema>>('DELETE', `/v1/goals/${id}`),
    },
    checkIns: {
      current: () => call<CheckInRow>('GET', '/v1/checkins/current'),
      history: () => call<{ items: CheckInRow[] }>('GET', '/v1/checkins'),
      submit: (id: string, b: Record<string, unknown>) =>
        call<Out<typeof okSchema>>('POST', `/v1/checkins/${id}/submit`, b),
      pending: () => call<{ items: CheckInRow[] }>('GET', '/v1/checkins/pending'),
      review: (id: string, response: string) =>
        call<Out<typeof okSchema>>('POST', `/v1/checkins/${id}/review`, { response }),
    },
    calendar: {
      range: (params: { from?: string; to?: string; clientId?: string } = {}) => {
        const q = new URLSearchParams(
          Object.entries(params).filter(([, v]) => v !== undefined) as [string, string][],
        ).toString();
        return call<{ from: string; to: string; items: CalendarItem[] }>('GET', `/v1/calendar${q ? `?${q}` : ''}`);
      },
      createEvent: (b: Record<string, unknown>) =>
        call<{ id: string; groupId: string | null; count: number }>('POST', '/v1/calendar/events', b),
      /** Zeljeno stanje popisa sudionika; server dodaje i mice razliku. */
      setParticipants: (groupId: string, clientIds: string[]) =>
        call<{ added: number; removed: number; total: number }>(
          'PUT', `/v1/calendar/groups/${groupId}/participants`, { clientIds },
        ),
      /** Klijent si sam upiše kad će trenirati; podsjetnik je zadano 60 min prije. */
      planWorkout: (b: {
        startsAt: string;
        workoutId?: string | null;
        title?: string | null;
        remindMinutesBefore?: number | null;
      }) => call<{ id: string }>('POST', '/v1/calendar/plan-workout', b),
      updateEvent: (id: string, b: { startsAt?: string; remindMinutesBefore?: number | null }) =>
        call<Out<typeof okSchema>>('PATCH', `/v1/calendar/events/${id}`, b),
      removeEvent: (id: string) => call<Out<typeof okSchema>>('DELETE', `/v1/calendar/events/${id}`),
    },
    series: {
      get: (metric: SeriesMetric, range: SeriesRange = '3m', clientId?: string) =>
        call<SeriesOutput>(
          'GET',
          `/v1/progress/series?metric=${metric}&range=${range}${clientId ? `&clientId=${clientId}` : ''}`,
        ),
      strength: (exerciseId: string) =>
        call<{ exerciseId: string; unit: string; points: { day: string; value: number }[] }>(
          'GET', `/v1/progress/series/strength/${exerciseId}`,
        ),
    },
    health: {
      /** Batch sinkronizacija iz Apple Healtha / Health Connecta. */
      sync: (samples: HealthSampleInput[]) =>
        call<{ inserted: number; skipped: number }>('POST', '/v1/health/samples', { samples }),
      today: () =>
        call<{ day: string; metrics: { metric: HealthMetric; value: number; unit: string }[] }>('GET', '/v1/health/today'),
    },
    achievements: {
      list: () => call<{ items: AchievementRow[] }>('GET', '/v1/achievements'),
    },
    summaries: {
      weekly: () => call<{ items: WeeklySummaryRow[] }>('GET', '/v1/summaries/weekly'),
    },
    content: {
      list: (category?: ContentCategory) =>
        call<{ items: ContentListItem[] }>('GET', `/v1/content${category ? `?category=${category}` : ''}`),
      read: (slug: string) => call<ContentDetail>('GET', `/v1/content/${slug}`),
    },
    discounts: {
      validate: (code: string, priceId?: string) =>
        call<DiscountValidation>('POST', '/v1/discounts/validate', { code, priceId }),
      list: () => call<{ items: DiscountCodeRow[] }>('GET', '/v1/admin/discount-codes'),
      create: (b: Record<string, unknown>) => call<{ id: string }>('POST', '/v1/admin/discount-codes', b),
      setActive: (id: string, active: boolean) =>
        call<Out<typeof okSchema>>('PATCH', `/v1/admin/discount-codes/${id}`, { active }),
    },
    metrics: () => call<BusinessMetrics>('GET', '/v1/admin/metrics'),
    trainerWeekly: () => call<{ weekStart: string | null; items: WeeklyClientRow[] }>('GET', '/v1/trainer/weekly'),
    raw: call,
  };
}

function mapStatus(status: number): ErrorCode {
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 422) return 'validation_failed';
  if (status === 429) return 'rate_limited';
  return 'internal';
}

export type ApiClient = ReturnType<typeof createClient>;
